import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { PRIMARY_BUTTON, Pill, SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import {
  useConversation,
  type ConversationMessage,
  type ConversationWindow,
} from '@/features/whatsapp/useConversation';
import { sendReplyErrorKind, useSendReply } from '@/features/whatsapp/useSendReply';

/**
 * ONE WHATSAPP CONVERSATION THREAD (slice 70c) — read the thread and reply inside
 * the 24 h window. A drill-down, not a nav item; the log links here.
 *
 * 🔴 SUPER-ADMIN ONLY — the gate is here as well as in the API (404) and the nav.
 * 🔴 NO OPTIMISTIC UI — a reply is shown only after the server confirms it (the
 *    mutation refetches the thread). A WhatsApp message cannot be unsent.
 * 🔴 ABSENCE OVER DISABLED — when the window is closed there is NO composer in the
 *    DOM at all, only an explanation. The window is enforced server-side; the
 *    countdown here is display only.
 * 🔴 The customer's text is rendered as PLAIN REACT TEXT (never dangerouslySet
 *    InnerHTML); React escapes it, which is the whole protection.
 */

const STATUS_TONE: Record<string, string> = {
  pending: 'border-raad-border text-raad-muted',
  sent: 'border-raad-primary/50 text-raad-accent',
  delivered: 'border-raad-marker-moving/40 text-raad-marker-moving',
  read: 'border-raad-marker-moving/40 text-raad-marker-moving',
  failed: 'border-raad-error/40 text-raad-error',
  received: 'border-raad-primary/50 text-raad-accent',
};

/** Whole hours+minutes left until the window closes, for the display countdown. */
function remaining(expiresAt: string | null): { h: number; m: number } | null {
  if (expiresAt === null) return null;
  const ms = Date.parse(expiresAt) - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  return { h: Math.floor(totalMin / 60), m: totalMin % 60 };
}

function Bubble({ m }: { m: ConversationMessage }) {
  const { t, i18n } = useTranslation();
  const outbound = m.direction === 'outbound';
  const rungs: Array<[string, string | null]> = [
    ['sent', m.sentAt],
    ['delivered', m.deliveredAt],
    ['read', m.readAt],
  ];
  const topRung = rungs.filter(([, at]) => at !== null).pop();
  return (
    <div className={outbound ? 'flex justify-end' : 'flex justify-start'}>
      <div
        data-testid="whatsapp-bubble"
        className={[
          'max-w-[85%] rounded-2xl border px-3 py-2 text-[13px]',
          outbound
            ? 'rounded-br-sm border-raad-primary/30 bg-raad-primary/10'
            : 'rounded-bl-sm border-raad-border bg-raad-panel',
        ].join(' ')}
      >
        {m.templateName !== null ? (
          <p className="text-[11.5px] text-raad-muted">
            {t('whatsapp.templateLabel')}: <span className="num">{m.templateName}</span>
          </p>
        ) : null}
        {m.bodyText !== null && m.bodyText !== '' ? (
          <p className="whitespace-pre-wrap break-words text-raad-text">{m.bodyText}</p>
        ) : m.templateName === null ? (
          <span className="text-raad-muted" aria-hidden>
            &mdash;
          </span>
        ) : null}

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px] text-raad-muted">
          <span className="num">{formatInstant(m.createdAt, i18n.language)}</span>
          {outbound && m.status === 'failed' && m.errorTitle ? (
            <span className="text-raad-error">
              {m.errorTitle}
              {m.errorCode ? ` (${m.errorCode})` : ''}
            </span>
          ) : outbound && topRung ? (
            <Pill label={t(`whatsapp.status.${topRung[0]}`)} tone={STATUS_TONE[topRung[0]] ?? ''} />
          ) : null}
          {outbound && m.sentByName ? (
            <span>{t('whatsapp.conversation.sentBy', { name: m.sentByName })}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Composer({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const mutation = useSendReply(conversationId);
  // Synchronous in-flight latch: isPending is React state and can lag a second
  // keydown, which would mint a second idempotency key and send twice.
  const inFlight = useRef(false);

  const trimmed = text.trim();
  const canSend = trimmed.length >= 1 && trimmed.length <= 4096 && !mutation.isPending;

  const submit = () => {
    if (!canSend || inFlight.current) return;
    inFlight.current = true;
    // A stable idempotency key per attempt; a resend after an error gets a new one.
    const idempotencyKey = `reply-${conversationId.slice(0, 24)}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    mutation.mutate(
      { text: trimmed, idempotencyKey },
      {
        onSuccess: () => setText(''),
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  const errorKind = mutation.isError ? sendReplyErrorKind(mutation.error) : null;

  return (
    <form
      className="mt-3 border-t border-raad-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="block">
        <span className="sr-only">{t('whatsapp.conversation.composerPlaceholder')}</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // E3: Enter inserts a newline; Ctrl/Cmd+Enter sends.
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              submit();
            }
          }}
          maxLength={4096}
          rows={3}
          placeholder={t('whatsapp.conversation.composerPlaceholder')}
          disabled={mutation.isPending}
          className="w-full resize-y rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-[13.5px] text-raad-text outline-none focus:border-raad-primary disabled:opacity-60"
        />
      </label>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-raad-muted">
          <span className="num">{text.length}</span> / 4096 · {t('whatsapp.conversation.sendHint')}
        </span>
        <button type="submit" className={PRIMARY_BUTTON} disabled={!canSend}>
          {mutation.isPending ? t('whatsapp.conversation.sending') : t('whatsapp.conversation.send')}
        </button>
      </div>
      {errorKind ? (
        <p role="alert" className="mt-2 text-[12px] text-raad-error">
          {t(`whatsapp.conversation.sendError.${errorKind}`)}
        </p>
      ) : null}
    </form>
  );
}

function WindowClosedNote() {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-lg border border-raad-border bg-raad-panel p-3 text-[12.5px] text-raad-muted">
      {t('whatsapp.conversation.windowClosed')}
    </div>
  );
}

function Countdown({ win }: { win: ConversationWindow }) {
  const { t } = useTranslation();
  // Re-render each minute so the countdown stays honest; display only.
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const left = remaining(win.expiresAt);
  if (!left) return null;
  return (
    <span className="text-[11.5px] text-raad-marker-moving">
      {t('whatsapp.conversation.windowCountdown', { h: left.h, m: left.m })}
    </span>
  );
}

export function Conversation() {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  const { conversationId = '' } = useParams();
  const q = useConversation(conversationId);
  const bottomRef = useRef<HTMLDivElement>(null);

  const first = q.data?.pages[0];
  const messages: ConversationMessage[] = useMemo(
    () => (q.data?.pages ?? []).flatMap((p) => p.messages),
    [q.data],
  );
  // Server is newest-first across pages; show oldest at the top.
  const ordered = useMemo(() => [...messages].reverse(), [messages]);

  useEffect(() => {
    // Guarded: scrollIntoView is unimplemented in jsdom (tests) and absent in some
    // embedded webviews — a missing scroll must never crash the thread.
    const el = bottomRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'end' });
  }, [ordered.length]);

  // Gate AFTER hooks (hooks must run unconditionally).
  if (state.status === 'authenticated' && state.user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const Header = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Link to="/whatsapp" className={`${SECONDARY_BUTTON} inline-flex items-center whitespace-nowrap`}>
        {t('whatsapp.conversation.back')}
      </Link>
      <h1 id="conversation-heading" className="font-display text-lg font-bold tracking-tight">
        {first ? (first.tenantName ?? t('whatsapp.unknownSender')) : t('whatsapp.conversation.title')}
      </h1>
      {first?.peerPhone ? <span className="num text-[12.5px] text-raad-muted">{first.peerPhone}</span> : null}
    </div>
  );

  if (q.isPending) {
    return (
      <section aria-labelledby="conversation-heading">
        {Header}
        <div className="mt-4 grid gap-3" role="status" aria-live="polite">
          <span className="sr-only">{t('whatsapp.conversation.loading')}</span>
          <div className="h-40 animate-pulse rounded-panel border border-raad-border bg-raad-panel" />
        </div>
      </section>
    );
  }

  if (q.isError || !first) {
    return (
      <section aria-labelledby="conversation-heading">
        {Header}
        <p role="alert" className="mt-4 text-sm text-raad-error">
          {t('whatsapp.conversation.error')}
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="conversation-heading" className="mx-auto max-w-2xl">
      {Header}

      {q.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? t('whatsapp.loadingMore') : t('whatsapp.conversation.loadOlder')}
          </button>
        </div>
      ) : null}

      <div className="mt-4 space-y-2">
        {ordered.length === 0 ? (
          <p className="text-sm text-raad-muted">{t('whatsapp.conversation.empty')}</p>
        ) : (
          ordered.map((m) => <Bubble key={m.id} m={m} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* Window open → composer + countdown. Closed → NO composer in the DOM. */}
      {first.window.open ? (
        <div className="mt-3">
          <Countdown win={first.window} />
          <Composer conversationId={conversationId} />
        </div>
      ) : (
        <WindowClosedNote />
      )}
    </section>
  );
}

export default Conversation;
